import { hasAsciiControlOrSpace } from '@/lib/validation';
import ky, { isHTTPError, isTimeoutError, type Hooks, type KyInstance, type Options } from 'ky';
import { authHeader, singleFlight, type AuthConfig, type TokenSource } from './auth';
import type { Envelope } from './envelopes';
import { ApiError } from './errors';
import { toSearchParams, type ArrayFormat, type QueryParams } from './search-params';

/** 서버 하나의 통신 규칙 — 서버를 추가할 때는 services.ts에 이 정의 하나를 더한다. */
export interface ServiceDefinition {
  /** 에러 메시지·로그에 쓰는 사람용 이름 */
  label: string;
  /** 없으면 요청 시 config 에러 — 다른 서버로 대신 보내지 않는다. */
  baseUrl: string | undefined;
  /** 주소를 넣는 환경변수 이름 — 미설정 안내용 */
  envKey: string;
  envelope: Envelope;
  auth: AuthConfig;
  arrayFormat: ArrayFormat;
  /** 기본 15초 */
  timeout?: number;
  /** 서버 전용 고정 헤더 — CORS 허용 헤더 목록에 있는지 서버와 먼저 확인한다. */
  headers?: Record<string, string>;
  /** 서버 전용 추가 동작(로깅·헤더 가공 등) — 공통 훅 뒤에 실행된다. */
  hooks?: Hooks;
  /** 테스트에서 fetch를 바꿔 끼우는 용도 */
  fetch?: typeof fetch;
  /** Set only from Vite's compile-time DEV value for the registered Core proxy. */
  allowDevelopmentCoreProxyHttp?: boolean;
}

export interface RequestOptions {
  /** Endpoint-specific success envelope; the default remains service-scoped. */
  responseEnvelope?: Envelope;
  params?: QueryParams;
  /** Return the accepted success envelope intact for endpoints whose metadata lives beside `data`. */
  preserveEnvelope?: boolean;
  /** Reject a successful response unless it uses this exact HTTP status. Unset keeps normal behavior. */
  expectedStatus?: number;
  json?: unknown;
  body?: BodyInit;
  headers?: Record<string, string>;
  cache?: RequestCache;
  /** TanStack Query queryFn의 signal을 넘기면 화면을 떠날 때 요청이 취소된다. */
  signal?: AbortSignal;
  timeout?: number;
}

export interface ServiceClient {
  readonly name: string;
  get: <T>(path: string, options?: RequestOptions) => Promise<T>;
  post: <T>(path: string, options?: RequestOptions) => Promise<T>;
  put: <T>(path: string, options?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, options?: RequestOptions) => Promise<T>;
  delete: <T>(path: string, options?: RequestOptions) => Promise<T>;
  /** 파일 다운로드처럼 봉투를 거치지 않는 응답 */
  blob: (path: string, options?: RequestOptions) => Promise<Blob>;
}

const DEFAULT_TIMEOUT = 15_000;

// 같은 토큰 출처를 여러 서버가 공유해도 갱신은 출처당 한 번만 돈다.
const refreshers = new WeakMap<
  TokenSource,
  { generation: number | undefined; run: () => Promise<boolean> }
>();
function refresherOf(tokens: TokenSource) {
  const { refresh } = tokens;
  if (!refresh) {
    return null;
  }
  const generation = tokens.getGeneration?.();
  let entry = refreshers.get(tokens);
  if (!entry || entry.generation !== generation) {
    // 세대가 다른 refresh 작업은 합치지 않는다. 이전 계정의 완료가 새 계정을 종료하면 안 된다.
    const run = singleFlight(async () => {
      try {
        return await refresh();
      } catch {
        return false;
      }
    });
    entry = { generation, run };
    refreshers.set(tokens, entry);
  }
  return entry.run;
}

type ParsedBody =
  | { type: 'empty' }
  | { type: 'json'; value: unknown }
  | { type: 'text'; value: string };

/** 응답 본문 읽기 — clone해 읽어 원본을 남긴다. JSON이 아니거나 깨졌으면 text로 구분해 돌려준다. */
async function parseBody(response: Response): Promise<ParsedBody> {
  if (response.status === 204) {
    return { type: 'empty' };
  }
  const text = await response.clone().text();
  if (!text) {
    return { type: 'empty' };
  }
  if (!response.headers.get('content-type')?.includes('json')) {
    return { type: 'text', value: text };
  }
  try {
    return { type: 'json', value: JSON.parse(text) as unknown };
  } catch {
    return { type: 'text', value: text };
  }
}

const bodyValue = (body: ParsedBody) => (body.type === 'empty' ? undefined : body.value);

async function toApiError(error: unknown, name: string, def: ServiceDefinition) {
  if (error instanceof ApiError) {
    return error;
  }
  if (isHTTPError(error)) {
    const detail = def.envelope.readError(bodyValue(await parseBody(error.response)));
    const status = error.response.status;
    return new ApiError({
      kind: 'http',
      service: name,
      status,
      code: detail.code,
      requestId: detail.requestId,
      authHandled: error.options.context.authHandled === true,
      message:
        status === 403
          ? '이 업무를 진행할 권한이 없습니다. 관리자에게 권한을 확인해 주세요.'
          : status >= 500
            ? '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.'
            : (detail.message ?? '요청을 처리하지 못했습니다.'),
      cause: error,
    });
  }
  if (isTimeoutError(error)) {
    return new ApiError({
      kind: 'timeout',
      service: name,
      message: '응답이 늦어 요청을 중단했습니다. 잠시 후 다시 시도해 주세요.',
      cause: error,
    });
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ApiError({
      kind: 'aborted',
      service: name,
      message: '요청이 취소되었습니다.',
      cause: error,
    });
  }
  // fetch는 연결 실패·CORS 차단을 TypeError로 던진다.
  if (error instanceof TypeError) {
    return new ApiError({
      kind: 'network',
      service: name,
      message: '연결하지 못했습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
      cause: error,
    });
  }
  // 토큰 조회 실패·훅 버그 등 — 네트워크 문제로 오인해 재시도하지 않게 따로 둔다.
  return new ApiError({
    kind: 'unknown',
    service: name,
    message: '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
    cause: error,
  });
}

function createInstance(def: ServiceDefinition): KyInstance {
  const { auth } = def;
  const commonHooks: Hooks = {
    beforeRequest: [
      async (request, options) => {
        assertScopedTarget(request.url, def);
        const generation = options.context.sessionGeneration;
        const header = await authHeader(auth);
        assertGeneration(auth, generation);
        if ((auth.scheme === 'bearer' || auth.scheme === 'raw') && auth.required && !header) {
          throw new ApiError({
            kind: 'http',
            service: 'core',
            status: 401,
            authHandled: true,
            message: '로그인이 필요합니다.',
          });
        }
        if (auth.scheme === 'bearer' || auth.scheme === 'raw')
          request.headers.delete('authorization');
        if (header) {
          request.headers.set(...header);
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        const read = ['GET', 'HEAD'].includes(request.method);
        const recoveryKey = `${request.method} ${new URL(request.url).pathname}`;
        if (response.ok && read && (auth.scheme === 'bearer' || auth.scheme === 'raw')) {
          assertGeneration(auth, options.context.sessionGeneration);
          auth.tokens.onReadSuccess?.(recoveryKey);
        }
        if (response.status !== 401 || (auth.scheme !== 'bearer' && auth.scheme !== 'raw')) {
          return;
        }
        assertGeneration(auth, options.context.sessionGeneration);
        const { onUnauthorized } = auth.tokens;
        // 인증 처리가 맡은 401이라는 표시 — 전역 에러 토스트가 같은 401을 다시 알리지 않게 한다.
        const handOver = () => {
          if (onUnauthorized) {
            options.context.authHandled = true;
            onUnauthorized();
          }
        };
        const sentHeader = request.headers.get('authorization');
        const currentHeader = read ? await authHeader(auth) : null;
        assertGeneration(auth, options.context.sessionGeneration);
        // 같은 세대에서 이미 교환된 토큰이 있으면 늦게 도착한 이전401은 refresh를 반복하지 않는다.
        const alreadyRefreshed = currentHeader !== null && currentHeader[1] !== sentHeader;
        const recoveryBlocked = read && auth.tokens.onReadUnauthorized?.(recoveryKey) === true;
        const refresh = refresherOf(auth.tokens);
        if (read && (alreadyRefreshed || (!recoveryBlocked && refresh && (await refresh())))) {
          assertGeneration(auth, options.context.sessionGeneration);
          const header = await authHeader(auth);
          assertGeneration(auth, options.context.sessionGeneration);
          if (!header) {
            handOver();
            return;
          }
          request.headers.set(...header);
          // 새 토큰으로 한 번만 다시 보낸다(이 요청은 훅을 다시 타지 않는다). 받은 응답을 그대로 돌려주면
          // 실패 시 바깥 ky가 HTTPError로 바꿔 준다.
          const retried = await ky(request, {
            retry: 0,
            timeout:
              (('timeout' in options ? options.timeout : undefined) as
                | number
                | false
                | undefined) ??
              def.timeout ??
              DEFAULT_TIMEOUT,
            fetch: scopedFetch(def, generationOf(auth)),
            throwHttpErrors: false,
            redirect: 'error',
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
          });
          assertGeneration(auth, options.context.sessionGeneration);
          if (retried.ok) auth.tokens.onReadSuccess?.(recoveryKey);
          if (retried.status === 401) {
            handOver();
          }
          return retried;
        }
        assertGeneration(auth, options.context.sessionGeneration);
        handOver();
      },
    ],
  };

  return ky.create({
    prefixUrl: def.baseUrl,
    redirect: 'error',
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    timeout: def.timeout ?? DEFAULT_TIMEOUT,
    // 재시도는 TanStack Query 한 곳에서만 한다 — 두 군데서 하면 재시도 횟수가 곱해진다.
    retry: 0,
    headers: def.headers,
    fetch: def.fetch,
    hooks: {
      beforeRequest: [...(commonHooks.beforeRequest ?? []), ...(def.hooks?.beforeRequest ?? [])],
      afterResponse: [...(commonHooks.afterResponse ?? []), ...(def.hooks?.afterResponse ?? [])],
      beforeRetry: def.hooks?.beforeRetry,
      beforeError: def.hooks?.beforeError,
    },
  });
}

function generationOf(auth: AuthConfig) {
  return auth.scheme === 'bearer' || auth.scheme === 'raw'
    ? auth.tokens.getGeneration?.()
    : undefined;
}
function assertGeneration(auth: AuthConfig, generation: unknown) {
  if (generation !== generationOf(auth)) {
    throw new ApiError({
      kind: 'aborted',
      service: 'core',
      message: '이전 세션의 요청을 취소했습니다.',
    });
  }
}
function scopedFetch(def: ServiceDefinition, generation: number | undefined): typeof fetch {
  return (input, init) => {
    const request = input instanceof Request ? input : new Request(input, init);
    // 사용자 hook이 Request를 반환해도 전송 직전 대상·세대 경계를 통과한다.
    assertScopedTarget(request.url, def);
    assertGeneration(def.auth, generation);
    const signal = init?.signal ?? request.signal;
    return (def.fetch ?? globalThis.fetch)(request, {
      redirect: 'error',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal,
    });
  };
}
function targetError() {
  return new ApiError({
    kind: 'config',
    service: 'core',
    message: '요청을 처리하지 못했습니다. 관리자에게 문의해 주세요.',
  });
}
function assertScopedTarget(target: string, def: ServiceDefinition) {
  if (!def.baseUrl) throw targetError();
  const origin = typeof location === 'undefined' ? 'https://local.invalid' : location.origin;
  const base = new URL(def.baseUrl, origin);
  const url = new URL(target, base);
  const coreProxyPath = '/__api/core/api/v1';
  const prefix = base.pathname.replace(/\/$/, '') + '/';
  if (
    url.origin !== base.origin ||
    (url.protocol !== 'https:' &&
      !(
        def.allowDevelopmentCoreProxyHttp === true &&
        url.protocol === 'http:' &&
        url.hostname === 'localhost' &&
        origin === url.origin &&
        base.pathname === coreProxyPath
      )) ||
    !url.pathname.startsWith(prefix) ||
    url.username ||
    url.password ||
    url.hash ||
    base.search ||
    base.hash
  )
    throw targetError();
}
function safePath(path: string) {
  if (path.startsWith('//') || /[?:#\\]/.test(path) || hasAsciiControlOrSpace(path))
    throw targetError();
  const relative = path.replace(/^\//, '');
  try {
    const decoded = decodeURIComponent(relative);
    if (
      decoded.includes('%') ||
      decoded.includes('\\') ||
      decoded.includes('?') ||
      decoded.includes('#') ||
      decoded.split('/').some((part) => part === '.' || part === '..') ||
      (decoded !== relative && /[/.]/.test(decoded))
    )
      throw targetError();
  } catch {
    throw targetError();
  }
  return relative;
}

/** 서버 정의 하나 → 요청 함수 묶음. 성공 본문은 봉투를 벗겨, 실패는 ApiError로 돌려준다. */
export function createServiceClient(name: string, def: ServiceDefinition): ServiceClient {
  let instance: KyInstance | null = null;

  const send = async (method: string, path: string, options: RequestOptions = {}) => {
    if (!def.baseUrl) {
      throw new ApiError({
        kind: 'config',
        service: name,
        message: '필요한 설정이 완료되지 않았습니다. 관리자에게 문의해 주세요.',
      });
    }
    const relative = safePath(path);
    if (
      options.params &&
      Object.keys(options.params).some((key) =>
        /^(access_token|refresh_token|code_verifier|authorization)$/i.test(key),
      )
    )
      throw targetError();
    const generation = generationOf(def.auth);
    instance ??= createInstance(def);
    // ky는 undefined 값도 "명시한 옵션"으로 병합해 인스턴스 기본값(서버별 timeout·headers)을 지운다 —
    // 값이 있는 옵션만 넘긴다.
    const kyOptions: Options = Object.fromEntries(
      Object.entries({
        method,
        context: { sessionGeneration: generation },
        fetch: scopedFetch(def, generation),
        json: options.json,
        body: options.body,
        headers: options.headers,
        cache: options.cache,
        signal: options.signal,
        timeout: options.timeout,
        searchParams: options.params ? toSearchParams(options.params, def.arrayFormat) : undefined,
      }).filter(([, value]) => value !== undefined),
    );
    try {
      // prefixUrl을 쓰는 ky는 경로가 "/"로 시작하면 에러를 낸다 — 호출부 실수를 여기서 흡수한다.
      const response = await instance(relative, kyOptions);
      assertGeneration(def.auth, generation);
      return response;
    } catch (error) {
      throw await toApiError(error, name, def);
    }
  };

  const json =
    (method: string) =>
    async <T>(path: string, options?: RequestOptions) => {
      const generation = generationOf(def.auth);
      const response = await send(method, path, options);
      const body = await parseBody(response);
      assertGeneration(def.auth, generation);
      if (options?.expectedStatus !== undefined && response.status !== options.expectedStatus) {
        throw new ApiError({
          kind: 'invalid-response',
          service: name,
          status: response.status,
          message: '자료를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
        });
      }
      if (body.type === 'empty') {
        return undefined as T;
      }
      // 성공인데 약속한 모양이 아니면 실패로 다룬다 — HTML(SPA fallback·잘못된 주소)을 데이터로 쓰지 않게.
      if (
        body.type === 'text' ||
        !(options?.responseEnvelope ?? def.envelope).accepts(body.value)
      ) {
        throw new ApiError({
          kind: 'invalid-response',
          service: name,
          status: response.status,
          message: '자료를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
        });
      }
      return (
        options?.preserveEnvelope
          ? body.value
          : (options?.responseEnvelope ?? def.envelope).unwrap(body.value)
      ) as T;
    };

  return {
    name,
    get: json('get'),
    post: json('post'),
    put: json('put'),
    patch: json('patch'),
    delete: json('delete'),
    blob: async (path, options) => (await send('get', path, options)).blob(),
  };
}
